import React, { useState, type JSX } from "react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Tooltip,
  IconName,
  LinkButton,
  Text,
} from "@fidelix/fx-miranda";
import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";

import { User, UserStatus } from "../dtos/user.model";
import MultiStatusBadge from "../common/multi-status-badge";
import { EntityService } from "../services/entity-service";
import InfoList from "../common/info-list";
import BasicDetails from "../common/basic-details";
import useToastManager from "../hooks/toast-manager";
import { formatPhoneNumber, formattedDate, getPaths } from "../helper";
import ConfirmationModal from "../common/confirmation-modal";
import { Invite } from "../dtos/invite.model";
import Loader from "./loader";
import { config } from "../config";
import { VALIDITY_TYPE } from "../constant";

interface IProps {
  userService: EntityService<User>;
  id: string;
  redirectUrl: string;
  deleteMessage: (email: string) => string;
  inviteService: EntityService<Invite>;
  showActions?: boolean;
  navigateBackLabel?: string;
  showUserStatus?: boolean;
  showInvitationInfo?: boolean;
}

interface IConfirmationInfo {
  label: string;
  icon: IconName;
  buttonLabel: string;
  dataTestId?: string;
}

const UserInfoView = ({
  id,
  userService,
  redirectUrl,
  deleteMessage,
  inviteService,
  showActions = true,
  navigateBackLabel,
  showUserStatus = true,
  showInvitationInfo = true,
}: IProps) => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [root] = getPaths();
  const isCompanyAdmin = !root.includes("manage");
  const { successToast, errorToast } = useToastManager();
  const [modal, setModal] = useState<JSX.Element | undefined>(undefined);
  const [isCopied, setIsCopied] = useState(false);
  const queryClient = useQueryClient();

  const { data: user = {} as User, isLoading } = useQuery({
    queryKey: ["get-user", id],
    queryFn: () => userService.getOne(+id),
  });

  const invitationPanel = {
    header: t`Invitation Info`,
    rows: [
      {
        label: t`Invited`,
        value: user.invites
          ? formattedDate(
              //A user can only have one invite at a time
              user?.invites[0]?.createdAt,
            )
          : "-",
      },
      {
        label: t`Invited By`,
        value: user.invites
          ? user.invites.map((invite: Invite) => invite.createdBy).toString()
          : "-",
      },
      {
        label: t`Validity`,
        value: user.invites
          ? formattedDate(
              //A user can only have one invite at a time
              user?.invites[0]?.expiredAt,
            )
          : "-",
      },
    ],
  };

  const loginPanel = {
    header: t`Log In Info`,
    rows: [
      {
        label: t`Last Log In`,
        value: user.lastLogin ? formattedDate(user.lastLogin) : "-",
      },
      {
        label: t`Registered`,
        value: user.createdAt ? formattedDate(user.createdAt) : "-",
      },
      {
        label: t`License and Users terms accepted`,
        value: user?.acceptedPrivacy
          ? formattedDate(user?.acceptedPrivacyAt)
          : "-",
      },
      {
        label: t`Privacy policy accepted`,
        value: user?.acceptedPrivacy
          ? formattedDate(user?.acceptedPrivacyAt)
          : "-",
      },
    ],
  };

  const PANELS = [
    {
      header: t`User Info`,
      rows: [
        { label: t`First name`, value: user?.firstName },
        { label: t`Last name`, value: user?.lastName },
        { label: t`Email`, value: user.email },
        {
          label: t`Phone`,
          value: user.phone ? formatPhoneNumber(user.phone) : "-",
        },
        ...(isCompanyAdmin
          ? [
              {
                label: t`Valid until`,
                value:
                  user.validityType === VALIDITY_TYPE.FIXED && user?.expiresAt
                    ? DateTime.fromISO(user.expiresAt).toLocaleString(
                        DateTime.DATE_SHORT,
                      )
                    : t`For now`,
              },
            ]
          : []),
      ],
    },
    ...[
      showInvitationInfo && user.status === UserStatus.Pending
        ? invitationPanel
        : loginPanel,
    ],
  ];

  const inviteKey = user.invites?.[0]?.inviteKey;
  const inviteUrl = `${config.ADMIN_URL}/invite-redirect/${inviteKey}`;

  const onCopyLink = () => {
    if (!inviteKey) return;
    navigator.clipboard.writeText(inviteUrl);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const onDelete = () => {
    setModal(
      <ConfirmationModal
        icon="trash"
        onConfirm={mutateOnDelete}
        setShowModal={() => setModal(undefined)}
        message={deleteMessage(user.email)}
      />,
    );
  };

  const onDeactivate = () => {
    setModal(
      <ConfirmationModal
        icon={icon}
        onConfirm={onUpdate}
        setShowModal={() => setModal(undefined)}
        message={label}
      />,
    );
  };

  const { mutate: mutateOnDelete } = useMutation({
    mutationFn: () => userService.delete(+userId),
    onSuccess: () => {
      successToast(t`User was deleted successfully.`);
      navigate(redirectUrl);
    },
    onError: () => {
      errorToast(t`User deletion failed.`);
    },
  });

  const { mutate } = useMutation({
    mutationFn: (data: User) => userService.update(+userId, data),
    onSuccess: () => {
      successToast(
        t`User ${user.active ? "deactivated" : "activated"} successfully.`,
      );
      setModal(undefined);
      queryClient.invalidateQueries({ queryKey: ["get-user"] });
    },
    onError: () => errorToast(t`Failed to update user.`),
  });

  const { mutate: mutateOnInvite, isPending } = useMutation({
    mutationFn: (data: { email: string; language?: string }) =>
      inviteService.create(data),
    onSuccess: () => {
      successToast(`${user.email} ${t`reinvited successfully.`}`);
      queryClient.invalidateQueries({ queryKey: ["get-user"] });
    },
    onError: () => {
      errorToast(`${t`Error! Email not sent for`} ${user.email}`);
    },
  });

  const onReinvite = () =>
    mutateOnInvite({ email: user.email, language: user.language });

  const onUpdate = () => {
    mutate(new User({ ...user, active: !user.active }));
  };

  const isReinvitable = (status: UserStatus) =>
    [UserStatus.Pending, UserStatus.Expired].includes(status);

  const confirmationInfo = (row: User): IConfirmationInfo => {
    if (isReinvitable(user.status)) {
      return {
        icon: "ticketFilled",
        buttonLabel: t`Reinvite`,
        label: "",
        dataTestId: "reinvite-button",
      };
    }
    if (row.active) {
      return {
        icon: "deactive",
        buttonLabel: t`Deactivate user`,
        label: t`When you deactivate a user, that user can no longer use the roles authorized by the company. You can reactivate the user at any time and enable the use of the services again.`,
        dataTestId: "deactive-button",
      };
    }
    return {
      icon: "plus",
      buttonLabel: t`Activate user`,
      label: t`When you reactivate the user, the user's roles come into effect again and users can use the services authorized by this company.`,
      dataTestId: "plus-button",
    };
  };

  const { label, icon, buttonLabel, dataTestId } = confirmationInfo(user);

  const goToUsers = () => {
    navigate(redirectUrl);
  };

  if (isLoading) return <Loader />;

  return (
    <>
      <BasicDetails
        title={`${user.firstName || ""} ${user.lastName || ""}`}
        description={user.email}
        navigateLabel={navigateBackLabel || t`Back to Users`}
        onNavigateBack={goToUsers}
      >
        <>
          {showUserStatus &&
            (user.status === "Pending" ? (
              <MultiStatusBadge
                status={
                  user.invites.some(
                    (invite: Invite) =>
                      new Date(invite.expiredAt).getTime() >
                      new Date().getTime(),
                  )
                    ? "Pending"
                    : "Denied"
                }
                label={
                  user.invites.some(
                    (invite: Invite) =>
                      new Date(invite.expiredAt).getTime() >
                      new Date().getTime(),
                  )
                    ? t`Pending`
                    : t`Expired`
                }
              />
            ) : (
              <MultiStatusBadge status={user.status} />
            ))}
        </>
      </BasicDetails>
      {modal}
      <InfoList
        panels={PANELS}
        actions={
          showActions ? (
            <>
              <LinkButton
                icon="edit"
                iconPlacement="left"
                variant="secondary"
                to={`${redirectUrl}/${id}/edit/info`}
              >
                {t`Edit user Info`}
              </LinkButton>
              <Button
                icon={icon}
                isLoading={isPending}
                isDisabled={isPending}
                iconPlacement="left"
                data-test-id={dataTestId}
                variant="secondary"
                onPress={isReinvitable(user.status) ? onReinvite : onDeactivate}
              >
                {buttonLabel}
              </Button>
              <Button
                icon="trash"
                iconPlacement="left"
                variant="secondary"
                onPress={onDelete}
              >
                {t`Remove user`}
              </Button>
              {isReinvitable(user.status) && user.invites[0]?.redeemUrl && (
                <Tooltip
                  content={
                    <Text color="white" variant="bodySmall">
                      {isCopied ? (
                        <Trans>Link Copied!</Trans>
                      ) : (
                        <Trans>Copy link</Trans>
                      )}
                    </Text>
                  }
                >
                  <Button
                    icon="duplicateCopy"
                    iconPlacement="left"
                    variant="secondary"
                    onPress={onCopyLink}
                  >
                    {t`Copy link`}
                  </Button>
                </Tooltip>
              )}
            </>
          ) : undefined
        }
      />
    </>
  );
};

export default UserInfoView;
